import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import type { User, Contract, Review, UserKinkProfile, UserToyProfile, KinkRating } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Lock, Skull, DollarSign, FileText, Star, Crown, Shield, Heart, Package, MessageCircle } from 'lucide-react';
import { ProviderIcon } from '@/components/icons/ProviderIcon';
import { getRoleLabel, getRoleBadgeColor } from '@/lib/roles';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';

const PROVIDER_LABELS: Record<string, string> = {
    google: 'Google',
    discord: 'Discord',
    github: 'GitHub',
    twitter: 'X / Twitter',
    microsoft: 'Microsoft',
};

const LINK_PRESETS: Record<string, string> = {
    cashapp: 'Cash App',
    venmo: 'Venmo',
    paypal: 'PayPal',
    twitter: 'Twitter / X',
    amazon: 'Amazon Wishlist',
    lovense: 'Lovense Wishlist',
    throne: 'Throne',
    onlyfans: 'OnlyFans',
    other: 'Other',
};

interface ProfileResponse {
    data: User & { private?: boolean };
    debt_stats?: {
        total_debt: number;
        total_minimum_payment: number;
        active_contracts_count: number;
        abandoned_contracts_count: number;
        lifetime_paid: number;
        total_fees_charged: number;
        total_taxes_assessed: number;
        total_interest_charged: number;
    };
    public_contracts?: Contract[];
    reviews?: Review[];
}

interface KinksProfileResponse {
    kink_bio: string | null;
    kinks: UserKinkProfile[];
    toys: UserToyProfile[];
}

interface OwnershipCheckResponse {
    is_owned_by: boolean;
    has_pending_request: boolean;
    has_owner: boolean;
}

const RATING_LABELS: Record<KinkRating, { label: string; color: string }> = {
    love: { label: 'Love it', color: 'bg-green-700/60 text-green-200' },
    like: { label: 'Like', color: 'bg-blue-700/60 text-blue-200' },
    curious: { label: 'Curious', color: 'bg-slate-600/60 text-slate-200' },
    soft_limit: { label: 'Soft limit', color: 'bg-orange-800/60 text-orange-200' },
    hard_limit: { label: 'Hard limit', color: 'bg-red-800/60 text-red-200' },
};

export default function UserProfilePage() {
    const { id } = useParams<{ id: string }>();
    const { user: currentUser } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [ownershipMessage, setOwnershipMessage] = useState('');
    const [showOwnershipForm, setShowOwnershipForm] = useState(false);

    // Redirect to settings if viewing own profile
    if (currentUser && id && String(currentUser.id) === String(id)) {
        return <Navigate to="/settings" replace />;
    }

    const { data, isLoading, error } = useQuery({
        queryKey: ['user-profile', id],
        queryFn: () => api.get<ProfileResponse>(`/profiles/${id}`),
    });

    const profile = data?.data;
    const canRequestOwnership = currentUser
        && profile
        && !profile.private
        && currentUser.id !== profile.id
        && (currentUser.role === 'sub' || currentUser.role === 'switch')
        && (profile.role === 'dom' || profile.role === 'switch');

    const { data: ownershipCheck } = useQuery({
        queryKey: ['ownership-check', id],
        queryFn: () => api.get<OwnershipCheckResponse>(`/ownership/check/${id}`),
        enabled: !!canRequestOwnership,
    });

    const { data: kinksProfile } = useQuery({
        queryKey: ['user-kinks-profile', id],
        queryFn: () => api.get<KinksProfileResponse>(`/profiles/${id}/kinks`),
        enabled: !!profile && !profile.private,
    });

    const isOtherUser = !!currentUser && !!profile && currentUser.id !== profile.id && !profile.private;

    const { data: canMessageData } = useQuery({
        queryKey: ['can-message', id],
        queryFn: () => api.get<{ can_message: boolean; conversation_id?: number }>(`/users/${id}/can-message`),
        enabled: isOtherUser,
    });

    const startConvoMutation = useMutation({
        mutationFn: (recipientId: number) =>
            api.post<{ data: { id: number } }>('/conversations', { recipient_id: recipientId, body: '👋' }),
        onSuccess: (data) => {
            navigate(`/messages?c=${data.data.id}`);
        },
        onError: (err: Error) => toast(err.message),
    });

    const handleMessage = () => {
        if (canMessageData?.conversation_id) {
            navigate(`/messages?c=${canMessageData.conversation_id}`);
        } else if (profile) {
            startConvoMutation.mutate(profile.id);
        }
    };

    const requestOwnershipMutation = useMutation({
        mutationFn: (message: string) =>
            api.post(`/ownership/request/${id}`, { message: message || null }),
        onSuccess: () => {
            trackEvent('ownership_requested', { target_user_id: Number(id) });
            toast('Ownership request sent!', 'success');
            setShowOwnershipForm(false);
            setOwnershipMessage('');
            queryClient.invalidateQueries({ queryKey: ['ownership-check', id] });
        },
        onError: (err: Error) => toast(err.message),
    });

    if (isLoading) {
        return <p className="text-center text-sm text-slate-500">Loading...</p>;
    }

    if (error) {
        return <p className="text-center text-sm text-red-400">Failed to load profile.</p>;
    }

    if (!profile) {
        return <p className="text-center text-sm text-slate-500">User not found.</p>;
    }

    const debtStats = data?.debt_stats;
    const publicContracts = data?.public_contracts ?? [];
    const reviews = data?.reviews ?? [];

    // Private profile view
    if (profile.private) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardContent className="flex flex-col items-center gap-4 py-12">
                        <Lock className="h-12 w-12 text-slate-600" />
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-3 mb-2">
                                {profile.avatar_path ? (
                                    <img
                                        src={profile.avatar_path}
                                        alt={profile.display_name}
                                        className="h-12 w-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-900/50 text-lg font-bold text-violet-300">
                                        {profile.display_name?.[0]?.toUpperCase() ?? '?'}
                                    </div>
                                )}
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-200">
                                        {profile.display_name}
                                    </h2>
                                    <Badge className={getRoleBadgeColor(profile.role)}>
                                        {getRoleLabel(profile.role)}
                                    </Badge>
                                </div>
                            </div>
                            <p className="text-sm text-slate-500 mt-4">
                                This profile is private.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const socialLinks = profile.social_links ?? {};
    const hasLinks = Object.keys(socialLinks).length > 0;

    return (
        <div className="space-y-6">
            {/* Profile header */}
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        {profile.avatar_path ? (
                            <img
                                src={profile.avatar_path}
                                alt={profile.display_name}
                                className="h-14 w-14 rounded-full object-cover"
                            />
                        ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-violet-900/50 text-xl font-bold text-violet-300">
                                {profile.display_name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                        )}
                        <div className="min-w-0">
                            <CardTitle className="text-lg">{profile.display_name}</CardTitle>
                            {profile.email && (
                                <p className="text-xs text-slate-400 mt-0.5">{profile.email}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                                <Badge variant="secondary">
                                    {getRoleLabel(profile.role)}
                                </Badge>
                                {profile.average_rating && (
                                    <span className="text-xs text-slate-400">
                                        {profile.average_rating} ({profile.review_count} reviews)
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {profile.bio ? (
                        <MarkdownRenderer content={profile.bio} />
                    ) : (
                        <p className="text-sm italic text-slate-500">No bio.</p>
                    )}

                    {/* Message button */}
                    {isOtherUser && canMessageData?.can_message && (
                        <div className="mt-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleMessage}
                                disabled={startConvoMutation.isPending}
                            >
                                <MessageCircle className="h-4 w-4 mr-1.5" />
                                Message
                            </Button>
                        </div>
                    )}

                    {/* Ownership display */}
                    {profile.owner && (
                        <div className="mt-3 flex items-center gap-2 text-sm">
                            <Crown className="h-4 w-4 text-amber-400" />
                            <span className="text-slate-400">Owned by</span>
                            <Link to={`/profiles/${profile.owner.id}`} className="text-violet-400 hover:underline">
                                {profile.owner.display_name}
                            </Link>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Request Ownership (subs/switches viewing a dom's profile) */}
            {canRequestOwnership && ownershipCheck && (
                <Card>
                    <CardContent className="py-4">
                        {ownershipCheck.is_owned_by ? (
                            <div className="flex items-center gap-2 text-sm text-amber-400">
                                <Crown className="h-4 w-4" />
                                <span>{profile.display_name} is your owner</span>
                            </div>
                        ) : ownershipCheck.has_pending_request ? (
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                                <Shield className="h-4 w-4" />
                                <span>Ownership request pending...</span>
                            </div>
                        ) : ownershipCheck.has_owner ? (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Crown className="h-4 w-4" />
                                <span>You already have an owner. Release current ownership to request a new one.</span>
                            </div>
                        ) : (
                            <>
                                {!showOwnershipForm ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-amber-700 text-amber-400 hover:bg-amber-900/20"
                                        onClick={() => setShowOwnershipForm(true)}
                                    >
                                        <Crown className="h-4 w-4 mr-1.5" />
                                        Request Ownership
                                    </Button>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-sm text-slate-300">
                                            Request <span className="text-violet-400">{profile.display_name}</span> as your owner
                                        </p>
                                        <textarea
                                            className="w-full rounded-md border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
                                            rows={3}
                                            placeholder="Include a message (optional)..."
                                            value={ownershipMessage}
                                            onChange={(e) => setOwnershipMessage(e.target.value)}
                                            maxLength={500}
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                className="bg-amber-600 hover:bg-amber-700"
                                                onClick={() => requestOwnershipMutation.mutate(ownershipMessage)}
                                                disabled={requestOwnershipMutation.isPending}
                                            >
                                                {requestOwnershipMutation.isPending ? 'Sending...' : 'Send Request'}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => { setShowOwnershipForm(false); setOwnershipMessage(''); }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Debt stats (subs only) */}
            {debtStats && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <DollarSign className="h-4 w-4 text-amber-400" />
                            Debt Stats
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            <div>
                                <p className="text-xs text-slate-500">Total Debt</p>
                                <p className="text-lg font-bold text-red-400">
                                    ${debtStats.total_debt.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Total Min. Payment</p>
                                <p className="text-lg font-bold text-amber-400">
                                    ${debtStats.total_minimum_payment.toFixed(2)}/period
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Lifetime Paid</p>
                                <p className="text-lg font-bold text-green-400">
                                    ${debtStats.lifetime_paid.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Fees & Taxes Paid</p>
                                <p className="text-lg font-bold text-orange-400">
                                    ${(debtStats.total_fees_charged + debtStats.total_taxes_assessed).toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Interest Charged</p>
                                <p className="text-lg font-bold text-rose-400">
                                    ${debtStats.total_interest_charged.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Active Contracts</p>
                                <p className="text-lg font-bold text-violet-400">
                                    {debtStats.active_contracts_count}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Abandoned Contracts</p>
                                <p className="text-lg font-bold text-red-500 flex items-center gap-1">
                                    <Skull className="h-4 w-4" />
                                    {debtStats.abandoned_contracts_count}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Public contracts */}
            {publicContracts.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <FileText className="h-4 w-4 text-violet-400" />
                            Public Contracts
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {publicContracts.map((contract) => (
                            <Link
                                key={contract.id}
                                to={`/contracts/${contract.id}`}
                                className="block rounded-lg border border-slate-800 p-3 transition-colors hover:border-slate-700"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-slate-200">
                                            {contract.title}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            Dom:{' '}
                                            <span className="text-violet-400">
                                                {contract.dom?.display_name}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <Badge
                                            variant="outline"
                                            className={
                                                contract.status === 'active'
                                                    ? 'border-green-700 text-green-400'
                                                    : contract.status === 'abandoned'
                                                      ? 'border-red-700 text-red-400'
                                                      : 'border-slate-700 text-slate-400'
                                            }
                                        >
                                            {contract.status}
                                        </Badge>
                                        <p className="mt-1 text-sm font-semibold text-amber-400">
                                            ${parseFloat(contract.current_balance).toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Reviews */}
            {reviews.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Star className="h-4 w-4 text-amber-400" />
                            Reviews ({profile.review_count ?? reviews.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {reviews.map((review) => (
                            <div key={review.id} className="border-b border-slate-800 pb-3 last:border-0 last:pb-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={star}
                                                className={`h-3 w-3 ${star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-xs text-slate-500">
                                        by {review.reviewer?.display_name}
                                    </span>
                                </div>
                                {review.body && (
                                    <p className="text-sm text-slate-300">{review.body}</p>
                                )}
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Linked accounts */}
            {profile.linked_accounts && profile.linked_accounts.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Linked Accounts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {profile.linked_accounts.map((acct) => (
                                <div key={acct.provider} className="flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-sm">
                                    <ProviderIcon provider={acct.provider} className="h-4 w-4" />
                                    <span className="text-slate-300">{PROVIDER_LABELS[acct.provider] ?? acct.provider}</span>
                                    {acct.username && (
                                        <span className="text-slate-500 text-xs">@{acct.username}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Kink Bio & Kinks */}
            {kinksProfile && (kinksProfile.kink_bio || kinksProfile.kinks.length > 0) && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Heart className="h-4 w-4 text-pink-400" />
                            Kinks & Limits
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {kinksProfile.kink_bio && (
                            <div className="rounded-lg bg-slate-800/50 p-3">
                                <MarkdownRenderer content={kinksProfile.kink_bio} />
                            </div>
                        )}
                        {kinksProfile.kinks.length > 0 && (() => {
                            const grouped = kinksProfile.kinks.reduce<Record<string, UserKinkProfile[]>>((acc, k) => {
                                (acc[k.category] ??= []).push(k);
                                return acc;
                            }, {});
                            return Object.entries(grouped).map(([category, kinks]) => (
                                <div key={category}>
                                    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        {category}
                                    </h4>
                                    <div className="space-y-1">
                                        {kinks.map((k) => (
                                            <div key={k.kink_id} className="flex items-center justify-between text-sm">
                                                <span className="text-slate-300">{k.kink_name}</span>
                                                <div className="flex gap-2">
                                                    {k.rating_a && (
                                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${RATING_LABELS[k.rating_a].color}`}>
                                                            {k.column_a_label}: {RATING_LABELS[k.rating_a].label}
                                                        </span>
                                                    )}
                                                    {k.rating_b && (
                                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${RATING_LABELS[k.rating_b].color}`}>
                                                            {k.column_b_label}: {RATING_LABELS[k.rating_b].label}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ));
                        })()}
                    </CardContent>
                </Card>
            )}

            {/* Toys / Collection */}
            {kinksProfile && kinksProfile.toys.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Package className="h-4 w-4 text-violet-400" />
                            Toy Inventory
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {(() => {
                            const grouped = kinksProfile.toys.reduce<Record<string, UserToyProfile[]>>((acc, t) => {
                                (acc[t.category] ??= []).push(t);
                                return acc;
                            }, {});
                            return Object.entries(grouped).map(([category, toys]) => (
                                <div key={category} className="mb-3 last:mb-0">
                                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        {category}
                                    </h4>
                                    <div className="flex flex-wrap gap-1.5">
                                        {toys.map((t) => (
                                            <Badge key={t.toy_id} variant="outline" className="border-slate-700 text-slate-300">
                                                {t.toy_name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            ));
                        })()}
                    </CardContent>
                </Card>
            )}

            {/* Social links */}
            {hasLinks && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Links & Wishlists</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-1.5">
                            {Object.entries(socialLinks).filter(([, v]) => v).map(([key, value]) => (
                                <div key={key} className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-500 min-w-[100px]">
                                        {LINK_PRESETS[key] ?? key}
                                    </span>
                                    {value.startsWith('http://') || value.startsWith('https://') ? (
                                        <a
                                            href={value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-violet-400 hover:underline flex items-center gap-1"
                                        >
                                            {value.replace(/^https?:\/\//, '').slice(0, 40)}
                                            <ExternalLink className="h-3 w-3" />
                                        </a>
                                    ) : (
                                        <span className="text-slate-200">{value}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
